import { createSelector } from '@reduxjs/toolkit';
import { selectTextForTextSearch, setTextSearchInSearchBoxSettingsState, setTravelDateInSearchBoxSettingsStateFromQueryParams } from 'store/slice/home';
import { closeAllOverlays, OverlayType, selectSortAndFiltersOverlayVisible, showOverlay } from 'store/slice/overlay';
import { selectConfigFromBootstrap, selectFeatureBehaviorFromBootstrap, selectFeatureExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { setSearchStateFromQueryParams } from 'component/page/search/store/search';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { selectFilterState, selectPageContentStatus, selectSearchState } from 'component/page/search/store/search.selectors';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesCloseCustomSheetClickEventActivitiesManifestV1,
    ActivitiesSortAndFilterOpenButtonClickEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import {
    ActivitiesCloseCustomSheetClickEventActivitiesFieldsV1,
    ActivitiesSortAndFilterOpenButtonClickEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { ExperimentId } from 'core/type/ExperimentId';
import { isB } from 'core/window/bootstrapConfig';
import { SuggestionResult } from 'core/type/SuggestionResult.types';

interface StateProps {
    config: BootstrapConfig;
    contentStatus: PageContentStatus;
    isLoading: boolean;
    isSortAndFilterOverlayShow: boolean;
    cityName: string;
    filteredResultNumber: number;
    pageTypeId: number;
    filterCount: number;
    screenSize: ScreenSize;
    isSearchBoxFeatureGroup: boolean;
    isNotMobile: boolean;
    isFixOverlayIssue: boolean;
    travelDate?: Date;
    isGlobalKeywordSearchEnabled: boolean;
}

interface DispatchProps {
    closeAllOverlays: () => void;
    onClickBackOverlay: () => void;
    onLocationSearchChange: (locationSearch: string, pageNumber: number) => void;
    onSortAndFilterButtonClick: () => void;
    setTextSearchState: (keyword: string) => void;
}

export type SearchPageProps = StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectTextForTextSearch,
    selectSearchState,
    selectPageContentStatus,
    selectSortAndFiltersOverlayVisible,
    selectFilterState,
    selectScreenSize,
    selectFeatureExperimentsFromBootstrap,
    selectFeatureBehaviorFromBootstrap,
    (config, searchText, state, contentStatus, isSortAndFilterOverlayShow, filter, screenSize, featureExperiments, featureBehaviours): StateProps => {
        const cityName = searchText;
        const filteredResultNumber = state.searchResponse?.result?.resultInfo?.totalFilteredActivities || 0;
        const filterCount =
            filter.valueFilterMatrix.reduce((count, valueFilter) => count + valueFilter.values.length, 0) +
            filter.rangeFilterMatrix.reduce((count, rangeFilter) => count + rangeFilter.ranges.length, 0);
        const isSearchBoxFeatureGroup = featureExperiments.featureBehavior.SearchBoxFeatureGroup;
        const isNotMobile = !screenSize.mobile;
        const isFixOverlayIssue = isB(ExperimentId.ACT_4894, config.experiments) && screenSize.mobile;

        return {
            config,
            cityName,
            contentStatus,
            filteredResultNumber,
            isSortAndFilterOverlayShow,
            filterCount,
            screenSize,
            isSearchBoxFeatureGroup,
            isNotMobile,
            isFixOverlayIssue,
            isLoading: state.isLoading,
            pageTypeId: config.pageConfig.pageTypeId,
            isGlobalKeywordSearchEnabled: featureBehaviours.GlobalKeywordSearch,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    closeAllOverlays: () => {
        dispatch(closeAllOverlays());
    },
    onClickBackOverlay: () => {
        trackElementEvent<ActivitiesCloseCustomSheetClickEventActivitiesFieldsV1>(ActivitiesCloseCustomSheetClickEventActivitiesManifestV1.ids, {});
        dispatch(closeAllOverlays());
    },
    onLocationSearchChange: (locationSearch: string, pageNumber: number) => {
        dispatch(setSearchStateFromQueryParams({ locationSearch, pageNumber }));
        dispatch(setTravelDateInSearchBoxSettingsStateFromQueryParams({ locationSearch }));
    },
    onSortAndFilterButtonClick: () => {
        dispatch(showOverlay(OverlayType.SortAndFilters));
        trackElementEvent<ActivitiesSortAndFilterOpenButtonClickEventActivitiesFieldsV2>(ActivitiesSortAndFilterOpenButtonClickEventActivitiesManifestV2.ids, {});
    },
    setTextSearchState: (keyword) => {
        dispatch(setTextSearchInSearchBoxSettingsState({ name: keyword } as SuggestionResult));
    },
});
